import styled from '@emotion/styled';
import Menu from 'common/Menu';
import { Button, Label, MenuItem, RotatableIcon } from 'common/styles';
import { IconDown } from 'common/icons';
import React, { useEffect, useRef, useState } from 'react';
import withStyleSystem from 'common/withStyleSystem';
import { SpacingProps } from 'common/withStyleSystem/withSpacingStyles';
import { inputSizeStyles } from './styles/Input';
import { InputSize } from './theme';
import ErrorMessage from './ErrorMessage';
import Collapse from './Collapse';
import { ValidationError } from './utils';
import { Icon } from './styles';
import { IconClose } from './icons';

export type SelectValue = string | number;

export type SelectInputProps<T = SelectValue> = {
  value: T;
  onChange?: (e: Event & { target: Event['target'] & { name: string; value: T; }; }) => void;
  onBlur?: (arg1: any) => void;
  children?: React.ReactElement | React.ReactElement[];
  renderValue?: React.ReactNode;
  placeholder?: string;
  clearable?: boolean;
  className?: string;
  name?: string;
  id?: string;
  label?: string;
  disabled?: boolean;
  inputSize?: InputSize;
  error?: string | null | ValidationError,
} & SpacingProps;

const SelectWrapper = styled.div<{ open?: boolean; }>(({ open }) => {
  let openStyles = {};
  if (open)
    openStyles = {
      [`& > [data-popper-placement^="bottom"] ~ ${SelectAnchor}`]: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
      '& > [data-popper-placement^="bottom"]': {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      },
      [`& > [data-popper-placement^="top"] ~ ${SelectAnchor}`]: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      },
      '& > [data-popper-placement^="top"]': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    };
  return {
    position: 'relative',
    ...openStyles,
  };
});

const SelectAnchor = styled(MenuItem)(({ theme, inputSize = 'default' }) => {
  const { padding, fontSize } = inputSizeStyles[inputSize];

  return ({
    minWidth: 120,
    borderRadius: theme.radius.medium,
    display: 'flex',
    justifyContent: 'space-between',
    padding,
    paddingLeft: '.5em',
    ...theme.font.responsiveSize(fontSize),
    [`${RotatableIcon}`]: {
      flexShrink: 0,
    },
    [theme.breakpoints.down('md')]: {
      minWidth: '100%',
    },
  });
});

const SelectMenu = styled(Menu)(({ theme }) => ({
  borderRadius: theme.radius.default,
  maxHeight: '30vh',
  width: '100%',
  overflowY: 'scroll',
  overflowX: 'hidden',
  ['&::-webkit-scrollbar']: {
    width: '2px',
  },
  ['&::-webkit-scrollbar-track']: {
    background: '#0f0f0f',
    borderRadius: '1rem',
  },
  ['&::-webkit-scrollbar-thumb']: {
    background: '#888',
    borderRadius: '1rem',
  },
  ['&::-webkit-scrollbar-thumb:hover']: {
    background: '#555',
  },
}));

const ClearButton = styled(Button)({
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

function SelectInput<T extends SelectValue = SelectValue>({
  children,
  value,
  onChange,
  renderValue,
  placeholder,
  clearable,
  className,
  name,
  id,
  label,
  disabled,
  inputSize,
  onBlur,
  error,
}: SelectInputProps<T>) {
  const [openState, setOpenState] = useState(false);
  const [minWidth, setMinWidth] = useState(120);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const childrenArray = React.Children.toArray(children);
  const open = disabled ? false : openState;

  let displaySingle: React.ReactNode = null;
  const childRefs = useRef([] as any[]);

  useEffect(() => {
    if (anchor && openState) {
      setMinWidth(anchor.clientWidth);
      // focus the first menu item after opening select
      const timerId = setTimeout(() => {
        childRefs.current[0]?.focus?.();
      }, 0);
      return () => clearTimeout(timerId);
    }
  }, [anchor, openState]);

  const handleClose = () => {
    setOpenState(false);
    // return focus to the anchor after closing
    anchor?.focus();
  };

  const handleItemClick = (child) => (event) => {
    // make sure the click event source is focusable
    if (!event?.currentTarget?.hasAttribute?.('tabindex')) {
      return;
    }
    const newValue = child.props.value;
    if (value !== newValue) {
      try {
        const nativeEvent = event.nativeEvent || event;
        const clonedEvent = new nativeEvent.constructor(nativeEvent.type, nativeEvent);
        Object.defineProperty(clonedEvent, 'target', {
          writable: true,
          value: { value: newValue, name },
        });
        onChange?.(clonedEvent);
      } catch (e) {
        onChange?.({ target: { value: newValue, name } } as any);
      }
      // close menu after clicking an item
      handleClose();
    }
  };

  const handleClear = (event) => {
    const nativeEvent = event.nativeEvent || event;
    const clonedEvent = new nativeEvent.constructor(nativeEvent.type, nativeEvent);
    Object.defineProperty(clonedEvent, 'target', {
      writable: true,
      value: { value: '', name },
    });
    onChange?.(clonedEvent);
    event.stopPropagation();
  };

  const handleKeyDown = (event) => {
    const validKeys = [' ', 'ArrowUp', 'ArrowDown', 'Enter'];
    if (validKeys.indexOf(event.key) !== -1) {
      event.preventDefault();
      setOpenState(true);
    }
  };

  const handleItemKeyDown = (child, index) => (event) => {
    event.preventDefault?.();
    const key = event.key;
    if (key === 'Enter') {
      handleItemClick(child)(event);
    }
    if (key === 'Escape' || key === 'Tab') {
      handleClose();
    }
    if (key === 'ArrowDown' && index < items.length - 1) {
      childRefs.current[index + 1]?.focus?.();
    }
    if (key === 'ArrowUp' && index > 0) {
      childRefs.current[index - 1]?.focus?.();
    }
  };
  const items = childrenArray.map((child, index) => {
    if (!React.isValidElement(child)) {
      return null;
    }

    const selected = String(value) === String(child.props.value);
    if (selected) {
      displaySingle = child.props.children;
    }

    return React.cloneElement(child, {
      'aria-selected': selected ? 'true' : undefined,
      onClick: handleItemClick(child),
      onKeyDown: handleItemKeyDown(child, index),
      ref: (el) => childRefs.current[index] = el,
      role: 'option',
      selected,
      value: undefined,
      'data-value': child.props.value,
    });
  });
  displaySingle = displaySingle || placeholder;
  const display = renderValue === undefined ? displaySingle : renderValue;
  const handleBlur = () => {
    onBlur && onBlur({
      target: {
        name,
        value,
      },
    });
  };
  return (
    <SelectWrapper className={className} open={open} onBlur={handleBlur}>
      {label && <Label htmlFor={id} mb={1}>{label}</Label>}

      <SelectAnchor disabled={disabled} as="div" variant="flat" fullWidth ref={setAnchor} inputSize={inputSize}
        onClick={() => setOpenState((prev) => !prev)}
        onKeyDown={handleKeyDown}
      >
        <input disabled={disabled} id={id} defaultValue={value} name={name} hidden />
        {/* display a zero width space if no item is selected */}
        {display || <>&nbsp;</>}
        <span>
          {clearable && value && <ClearButton type="button" variant="flat" p="0" ml="0.25rem" onClick={handleClear}>
            <Icon cursor="pointer">
              <IconClose />
            </Icon>
          </ClearButton>}
          <RotatableIcon trigger={open} cursor="pointer"><IconDown /></RotatableIcon>
        </span>
      </SelectAnchor>

      <Collapse open={!!error}>
        <ErrorMessage mt={1} field={label} message={error} />
      </Collapse>

      <SelectMenu
        as="ul"
        px={0}
        py={0}
        offsetY={1}
        showArrow={false}
        style={{ minWidth }}
        open={open}
        anchor={anchor}
        onClose={() => setOpenState(false)}
      >
        {items}
      </SelectMenu>
    </SelectWrapper>
  );
}

export default withStyleSystem(styled(SelectInput)({}));
